import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TaggingOptions, UiKitModule } from '@serviceplan/ui-kit';
import {
  accessAsset,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  CommonUtilityLib,
  ENVIRONMENT,
  ValueListsService,
} from '@thehubz/client/core';
import { Asset, AssetsApiService, NotifyOptions, UserInfo, UsersTaggingApiService, UserValue } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { filter, Subscription } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { AssetMetadata, FormFields } from '../../asset-info/models/asset-info.interface';
import { AssetNotificationsComponent } from '../../asset-notifications/asset-notifications.component';
import { CustomAssetFieldsComponent } from '../../custom-asset-fields/custom-asset-fields.component';

@Component({
  selector: 'thehubz-asset-usage-right-metadata',
  templateUrl: './asset-usage-right-metadata.component.html',
  styleUrls: ['./asset-usage-right-metadata.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    ClientCoreModule,
    UiKitModule,
    AssetNotificationsComponent,
    CustomAssetFieldsComponent,
  ],
})
export class AssetUsageRightMetadataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() assetInfo!: AnyType;

  /**
   * custFieldsRef is used to get the formdata from child component
   */
  @ViewChild('custFieldsRef') custFieldsRef!: CustomAssetFieldsComponent;
  editFlag = false;
  assetInfoObject!: AssetMetadata;
  updateAssetForm: FormGroup;
  users!: UserValue[];
  descLength = 0;
  checkDescLength = true;
  todayDate = new Date();
  userSelected?: UserValue;
  taggingOptions: TaggingOptions = {
    roles: ['global.library', 'asset.update'],
  };
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text
  brandingKey!: CustomClients;
  CustomClients = CustomClients;
  checked = false;

  userACBSubjectSubscription: Subscription | undefined;

  AccessAsset = accessAsset;

  /**
   * Return the form control value for expiryDate
   */
  get expiryDate() {
    return this.updateAssetForm.get('expiryDate')?.value;
  }

  constructor(
    @Inject(ENVIRONMENT) readonly env: ClientEnvironment,
    private _formBuilder: FormBuilder,
    private valueListsService: ValueListsService,
    private assetsService: AssetsApiService,
    private commonUtilityLib: CommonUtilityLib,
    protected assetCommonService: AssetCommonService,
    private usersTaggingApiService: UsersTaggingApiService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = this.env.brandingKey ?? CustomClients.Standard;
    this.updateAssetForm = this._formBuilder.group({
      owner: ['', Validators.required],
      notes: '',
      expiryDate: ['', Validators.required],
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.assetInfo = changes['assetInfo'].currentValue;
    this.getUsers();
    if (this.assetInfoObject) {
      this.assetInfoObject.metadata.forEach((obj: AnyType) => {
        if (Object.keys(this.updateAssetForm.value).indexOf(obj.formControlName) !== -1) {
          if (obj.editable) {
            this.updateAssetForm.controls[obj.formControlName].enable();
          } else {
            this.updateAssetForm.controls[obj.formControlName].disable();
          }
        }
      });
    }
    this.createAssetInfoObj();
  }

  ngOnInit(): void {
    this.assetCommonService.metadataEditable.next(this.editFlag);
  }

  /**
   * Object Checking form Accessibility
   */
  createAssetInfoObj() {
    this.assetInfoObject = {
      customerName: CustomClients.Standard,
      metadata: [
        {
          fieldLabel: $localize`Owner`,
          editable: true,
          formControlName: 'owner',
          options: [],
        },
        {
          fieldLabel: $localize`Expiry date`,
          editable: true,
          formControlName: 'expiryDate',
        },
        {
          fieldLabel: $localize`Source and notes`,
          editable: true,
          formControlName: 'notes',
        },
      ],
    };

    if (this.brandingKey === CustomClients.Arval) {
      this.assetInfoObject.customerName = CustomClients.Arval;
      const customMetadata: FormFields[] = [
        {
          fieldLabel: $localize`Copyright`,
          editable: true,
          formControlName: 'copyright',
        },
        {
          fieldLabel: 'Land',
          editable: true,
          formControlName: 'country',
        },
        {
          fieldLabel: 'Anbieter',
          editable: true,
          formControlName: 'provider',
        },
        {
          fieldLabel: 'Quelle',
          editable: true,
          formControlName: 'source',
        },
      ];
      customMetadata.forEach((metadata) => {
        this.updateAssetForm.addControl(metadata.formControlName, new FormControl(''));
      });
      this.assetInfoObject.metadata.splice(1, 0, ...customMetadata);
    }
    this.patchFormValues();
  }

  /**
   * Patch form fields
   */
  patchFormValues() {
    if (!this.commonUtilityLib.isEmptyObject(this.assetInfo)) {
      this.assetCommonService.patchFormValues(this.assetInfo, this.updateAssetForm);
      if (this.assetInfo.expiryDate) {
        this.checkboxChanged({ checked: false }, this.assetInfo.expiryDate);
      } else {
        this.checkboxChanged({ checked: true }, this.assetInfo.expiryDate);
      }
    }
  }

  onDateSelection(evt: MatDatepickerInputEvent<AnyType, AnyType>) {
    this.assetInfo.assetExpiryDays = this.assetCommonService.getAssetExpireDays(evt.value);
    this.checked = false;
    const expiryDate = this.updateAssetForm.controls['expiryDate'];
    expiryDate?.setValidators(Validators.required);
    expiryDate.updateValueAndValidity();
  }

  /**
   * Get list of all user asset ACB
   */
  getUsers() {
    this.userACBSubjectSubscription = this.assetCommonService.userACBSubject$.pipe(filter((data) => data.length > 0)).subscribe((users) => {
      this.users = users;
      const owner = this.users.find((userInfo: AnyType) => userInfo.id === this.assetInfo.owner);
      if (owner) {
        this.userSelected = owner;
      }
    });
  }

  /**
   *  This function will update the asset usage right as well as custom fields
   *  elements which are coming from custom fields child components
   */
  saveAssetInfo() {
    if (this.updateAssetForm.valid) {
      const formValues = this.updateAssetForm.getRawValue();
      formValues.owner = formValues.owner.id;
      const customDataFields = this.custFieldsRef.createCustomFieldForm.getRawValue();
      formValues['customFields'] = {};
      if (this.assetInfo && this.assetInfo.customFields) {
        formValues['customFields'] = this.assetInfo.customFields;
      }
      Object.keys(customDataFields).forEach(function (key) {
        formValues['customFields'][key] = customDataFields[key];
      });
      if (this.brandingKey === CustomClients.Arval) {
        this.assetInfoObject.metadata.forEach((meta) => {
          const keysToExclude = ['expiryDate', 'notes', 'owner'];
          if (Object.prototype.hasOwnProperty.call(formValues, meta.formControlName) && !keysToExclude.includes(meta.formControlName)) {
            formValues['customFields'][meta.formControlName] = formValues[meta.formControlName];
            delete formValues[meta.formControlName];
          }
        });
      }
      this.updateAssetInfo(formValues);
    }
  }

  /**
   * Enable edit mode
   */
  editInformation() {
    this.editFlag = !this.editFlag;
    this.assetCommonService.metadataEditable.next(this.editFlag);
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  /**
   * Update asset with usage right data
   * @param formData \
   */
  updateAssetInfo(formData: AnyType) {
    this.assetsService.assetsControllerUpdate(this.assetInfo?._id, formData).subscribe({
      next: (data: Asset) => {
        if (data) {
          // notify users tagged in sp-editor component
          if (this.taggedUser && this.taggedUser.length) {
            const notifyOptions: NotifyOptions = {
              userInfo: this.taggedUser,
              callFromContext: '',
              contextPath: 'asset',
              contextId: data._id,
              contextName: data.name,
              editorDescription: this.descriptionText,
            };
            this.usersTaggingApiService.usersTaggingControllerNotifyTaggedUsers(notifyOptions).subscribe();
          }
          // deleting currentVersion from data (object returned from update)
          // as update doesn't populate currentVersion
          // it create issue with aspect ratios tab
          delete data.currentVersion;
          this.assetInfo = { ...this.assetInfo, ...data };
          this.patchFormValues();
          this.assetCommonService.assetDataSubject$.next(this.assetInfo);
          this.editFlag = !this.editFlag;
          this.assetCommonService.metadataEditable.next(this.editFlag);
          this.clientNotificationHandlerService.successNotification(`${data.name} successfully updated`);
        }
      },
      error: (err) => {
        this.clientNotificationHandlerService.errorNotification(err);
      },
    });
  }

  checkboxChanged(event: AnyType, expiryDateValue?: string) {
    this.checked = event.checked;
    const expiryDate = this.updateAssetForm.controls['expiryDate'];
    if (event.checked) {
      expiryDate.clearValidators();
      expiryDate.setValue(expiryDateValue ? expiryDateValue : '');
    } else {
      expiryDate?.setValidators(Validators.required);
      expiryDate.setValue(expiryDateValue ? expiryDateValue : '');
    }
    expiryDate.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    if (this.userACBSubjectSubscription) this.userACBSubjectSubscription.unsubscribe();
  }
}
